import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import { useHomeFilters } from '@/hooks/useHomeFilters';
import { useAuth } from '@/contexts/AuthContext';
import CardSection from '@/components/home/CardSection';
import AdminControls from '@/components/home/AdminControls';
import TableDisplay from '@/components/home/TableDisplay';
import HomeStatus from '@/components/home/HomeStatus';
import ResultsHandler from '@/components/home/ResultsHandler';
import LocationStateHandler from '@/components/home/LocationStateHandler';
import { useHomePageHandlers } from '@/components/home/handlers/HomePageHandlers';

const Home = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { 
    selectedCountry, 
    selectedLeague, 
    selectedYear,
    showTable,
    tableData,
    sortColumn,
    sortDirection,
    resultsCountry,
    resultsLeague,
    orderedCountries,
    availableLeagues,
    availableYears,
    resultsAvailableLeagues,
    isLoading,
    isError,
    setSelectedCountry,
    setSelectedLeague,
    setSelectedYear,
    setResultsCountry,
    setResultsLeague,
    handleYearSelect,
    handleSort,
    getFilteredAndSortedData,
    setTableData,
    setShowTable,
  } = useHomeFilters();

  const {
    handleResultsLeagueSelect,
    handleCountrySelect,
    handleTabChange,
    handleLatestClick
  } = useHomePageHandlers();

  // Show loading or error states
  if (isLoading || isError) {
    return <HomeStatus isLoading={isLoading} isError={isError} />;
  }

  // Handle location state (when navigating back from another page)
  return (
    <>
      <LocationStateHandler
        setTableData={setTableData}
        setShowTable={setShowTable}
        setSelectedCountry={setSelectedCountry}
        setSelectedLeague={setSelectedLeague}
        setSelectedYear={setSelectedYear}
      />

      <ResultsHandler 
        resultsCountry={resultsCountry} 
        resultsLeague={resultsLeague} 
      />
  
      {/* Render league table if selected */}
      {showTable && tableData ? (
        <TableDisplay
          tableData={tableData}
          filteredAndSortedData={getFilteredAndSortedData()}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          selectedCountry={selectedCountry || ''}
          selectedLeague={selectedLeague || ''}
          selectedYear={selectedYear || ''}
        />
      ) : (
        // Main home page
        <>
          <Header />
          <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 sm:p-6">
            <div className="container mx-auto max-w-7xl">
              <CardSection 
                selectedCountry={selectedCountry}
                selectedLeague={selectedLeague}
                selectedYear={selectedYear}
                resultsCountry={resultsCountry}
                resultsLeague={resultsLeague}
                orderedCountries={orderedCountries}
                availableLeagues={availableLeagues}
                availableYears={availableYears}
                resultsAvailableLeagues={resultsAvailableLeagues}
                onCountrySelect={handleCountrySelect(setSelectedCountry)}
                onLeagueSelect={setSelectedLeague}
                onYearSelect={handleYearSelect}
                setResultsCountry={setResultsCountry}
                handleResultsLeagueSelect={handleResultsLeagueSelect(setResultsLeague)}
                handleTabChange={handleTabChange}
                handleLatestClick={handleLatestClick}
              />

              <AdminControls isAdmin={isAdmin} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Home;
